import _ from 'lodash';
import orComparator from '../../../shared/util/lodash/merge/orComparator';
import useMyWorkspace from '../../workspace/hooks/useMyWorkspace';
import AlarmAPI from '../AlarmAPI';
import { UseQuerySubscription } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useMemo } from 'react';
import StructureAPI from '../../structure/StructureAPI';
import { getDevicesIdsFromGroupSelector } from '../../structure/selectors/Group.selector';
import { useAppSelector } from '../../store.model';
import { ALARMS_POLLING_INTERVAL, AlarmState } from '../Alarm.model';
import { getFeatureToggle } from '../../../shared/featureToggle';

type Options = Parameters<UseQuerySubscription<any>>[1];

function useAlarmsByGroup(
  args: {
    groupId?: string;
  },
  options: Options = {}
) {
  const { data: workspaceData, ...restWorkspaceAPI } = useMyWorkspace(
    undefined,
    options
  );
  const featureToggle = useAppSelector(getFeatureToggle);
  const { data: alarmsData, ...restAlarmAPI } =
    AlarmAPI.useQueryMyIncidentByWorkspaceIdQuery(
      {
        workspaceId: workspaceData?.id ?? '',
        params: {
          state: AlarmState.NEW,
          group: args?.groupId ?? '',
          page_size: 1000,
        },
      },
      {
        skip: !workspaceData?.id,
        pollingInterval: ALARMS_POLLING_INTERVAL,
        ...options,
      }
    );

  const { data: devicesAPI, ...restDevicesAPI } =
    StructureAPI.useGetStructureV2Query(undefined, options);
  const devicesIdsFromGroup = useAppSelector((state) =>
    getDevicesIdsFromGroupSelector(state, {
      workspaceId: workspaceData?.id,
      groupId: args?.groupId ?? '',
    })
  );

  const data = useMemo(
    () =>
      alarmsData?.data
        ?.filter?.((alarm) => devicesIdsFromGroup?.includes(alarm.deviceId))
        .filter(
          (alarm) =>
            !featureToggle.HideDisconnectionAlarms ||
            (featureToggle.HideDisconnectionAlarms &&
              alarm?.event !== '_disconn')
        ),
    [alarmsData, devicesIdsFromGroup, featureToggle.HideDisconnectionAlarms]
  );

  const allAlarms = useMemo(
    () =>
      alarmsData?.data?.filter?.((alarm) =>
        devicesIdsFromGroup?.includes(alarm.deviceId)
      ),
    [alarmsData, devicesIdsFromGroup]
  );

  const response = _.chain(restAlarmAPI)
    .mergeWith(restDevicesAPI, orComparator)
    .mergeWith(restWorkspaceAPI, orComparator)
    .value();
  return {
    ...response,
    data,
    allAlarms,
  };
}

export default useAlarmsByGroup;
